import { isDevMock } from '../dev/isDevMock'
import { authMeUrl } from './authMeUrl'

export class AuthError extends Error {
  constructor() {
    super('Authentication required')
    this.name = 'AuthError'
  }
}

let verifyingAuth: Promise<boolean> | null = null

function verifySessionExpired(): Promise<boolean> {
  if (!verifyingAuth) {
    verifyingAuth = fetch(authMeUrl())
      .then(async (r) => {
        if (!r.ok) return false
        const data = (await r.json()) as { user: unknown }
        return !data.user
      })
      .catch(() => true)
      .finally(() => {
        verifyingAuth = null
      })
  }
  return verifyingAuth
}

const AUTH_REDIRECT_KEY = 'loopqa:auth-redirect'
const AUTH_REDIRECT_WINDOW_MS = 60_000
const AUTH_REDIRECT_MAX = 3

/**
 * Rate-limits sending the browser to `/api/auth-login`, so a login that comes back still
 * unauthenticated (a broken session, a user with no account) can't put the tab in a redirect loop.
 * Shared with the page-level redirect in useSignInOnUnauthorized: both are "we bounced this tab to
 * login", and once the budget is spent the surfaces fall back to their in-page Sign in button.
 */
export function canRedirectToLogin(): boolean {
  try {
    const raw = sessionStorage.getItem(AUTH_REDIRECT_KEY)
    const now = Date.now()
    if (raw) {
      const { count, ts } = JSON.parse(raw) as { count: number; ts: number }
      if (now - ts < AUTH_REDIRECT_WINDOW_MS) {
        if (count >= AUTH_REDIRECT_MAX) return false
        sessionStorage.setItem(AUTH_REDIRECT_KEY, JSON.stringify({ count: count + 1, ts }))
        return true
      }
    }
    sessionStorage.setItem(AUTH_REDIRECT_KEY, JSON.stringify({ count: 1, ts: now }))
    return true
  } catch {
    return true
  }
}

// One automatic login bounce per tab until a signed-in identity resolves: a bounce that completes
// the provider round-trip and lands back signed out has answered the question, and repeating it
// re-boots the whole SPA (re-downloading the immutable entry bundle on every boot — issue #6730)
// for the same result. markAuthenticated clears the marker; user gestures never consult it.
const LOGIN_BOUNCE_KEY = 'loopqa:login-bounce'

/** Marks that this tab is being sent through an automatic (no user gesture) login bounce. */
export function markLoginBounce(): void {
  try {
    sessionStorage.setItem(LOGIN_BOUNCE_KEY, String(Date.now()))
  } catch {
    // Storage unavailable — the redirect budget above still bounds the loop.
  }
}

/** True once an automatic login bounce already ran in this tab without producing a session. */
export function loginBounceSpent(): boolean {
  try {
    return sessionStorage.getItem(LOGIN_BOUNCE_KEY) !== null
  } catch {
    return false
  }
}

let wasEverAuthenticated = false

export function markAuthenticated() {
  wasEverAuthenticated = true
  try {
    sessionStorage.removeItem(LOGIN_BOUNCE_KEY)
  } catch {
    // Nothing to clear without storage.
  }
}

/**
 * What the dev fixtures need to route a call: its method and JSON body. Direct apiFetch callers pass
 * those in `init`, but RTK Query's fetchFn is handed a single fully-built Request and no init — so
 * without reading them back off that Request, every mutation looked like a GET to the fixtures and
 * was answered with the unchanged record (see mockProjectResponse's method dispatch).
 */
async function fixtureRequestInit(input: RequestInfo, init?: RequestInit): Promise<RequestInit | undefined> {
  if (init || !(input instanceof Request)) return init
  if (input.method === 'GET') return { method: 'GET' }
  return { method: input.method, body: await input.clone().text() }
}

async function rawApiFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
  // In dev-mock mode there is no backend. RTK Query supplies the project/account request boundary,
  // while the admin dashboard/charts call apiFetch directly, so serve both from in-memory fixtures.
  // Non-admin (or unmocked) paths fall through.
  if (isDevMock()) {
    const [{ mockProjectResponse }, { mockAdminResponse }] = await Promise.all([
      import('../dev/mockProjectResponse'),
      import('../dev/mockAdmin'),
    ])
    const fixtureInit = await fixtureRequestInit(input, init)
    const mocked = mockProjectResponse(url, fixtureInit) ?? mockAdminResponse(url, fixtureInit)
    if (mocked) return mocked
  }
  return fetch(input, init)
}

// A 401 for a session that HAD been authenticated means it expired mid-visit; send the tab back
// through login so it lands on the same URL signed in. A visitor who was never authenticated is left
// alone here — whether a 401 should interrupt them depends on the page, not the request (a signed-out
// visitor on a public project also 401s on member-only side requests), so that call is made by the
// surface via useSignInOnUnauthorized.
async function redirectIfSessionExpired() {
  if (!wasEverAuthenticated) return
  const expired = await verifySessionExpired()
  if (expired && !loginBounceSpent() && canRedirectToLogin()) {
    markLoginBounce()
    const returnTo = window.location.pathname + window.location.search
    window.location.href = `/api/auth-login?returnTo=${encodeURIComponent(returnTo)}`
  }
}

export async function apiFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const res = await rawApiFetch(input, init)
  if (res.status === 401) {
    await redirectIfSessionExpired()
    throw new AuthError()
  }
  return res
}

/**
 * apiFetch for RTK Query's `fetchFn`: it RETURNS the 401 response instead of throwing.
 * fetchBaseQuery reports anything the fetchFn throws as `{ status: 'FETCH_ERROR' }`, which erased
 * the status — so a page could not tell "you need to sign in" from "the network failed", and every
 * 401-gated message and Sign in button in the app was unreachable. Endpoint errors now carry
 * `status: 401`; see isUnauthorizedError.
 */
export async function apiFetchForQuery(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const res = await rawApiFetch(input, init)
  if (res.status === 401) await redirectIfSessionExpired()
  return res
}
